import React from "react";
import { Autocomplete, Checkbox, MenuItem, TextField } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MultiselectProps from "./Multiselect.types";
import { useTranslation } from "react-i18next";
import { Virtuoso } from "react-virtuoso";
import "./Multiselect.scss";

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, role, ...other } = props;
  const data = children as React.ReactElement[];

  const itemCount = Array.isArray(children) ? children.length : 0;
  const itemSize = 42;

  function getHeight() {
    if (itemCount > 5) {
      return 200;
    }
    return itemCount * itemSize;
  }

  return (
    <div ref={ref}>
      <div {...other} style={{ height: getHeight() }}>
        <Virtuoso
          className="custom-scrollbar"
          data={data}
          itemContent={(index, child) => {
            return React.cloneElement(child, { index });
          }}
        />
      </div>
    </div>
  );
});

function Multiselect(props: MultiselectProps) {
  const { t } = useTranslation();
  // component state
  const [value, setValue] = React.useState<any[]>([]);
  const hasValue = value?.length > 0;

  const inputValue = t("multiselect.placeholder", {
    count: value?.length,
    placeholder: props.placeholder
  });

  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const sortedOptions = [];
  props.options.forEach((option) => {
    if (value.includes(option)) {
      sortedOptions.unshift(option);
    } else {
      sortedOptions.push(option);
    }
  });

  return (
    <Autocomplete
      value={value}
      size="small"
      componentsProps={{
        paper: {
          sx: {
            width: "350px"
          }
        }
      }}
      fullWidth
      onInputChange={(_e, _, reason) => {
        if (reason === "clear") {
          props.onClose([]);
        }
      }}
      disableListWrap
      ListboxComponent={ListboxComponent}
      multiple
      options={sortedOptions}
      disableCloseOnSelect
      onClose={() => {
        props.onClose(value);
      }}
      onChange={(_event, newValue) => {
        setValue(newValue);
      }}
      classes={{ input: hasValue ? "multiselect" : "" }}
      getOptionLabel={props.getOptionLabel}
      renderOption={(optionProps, option, { selected }) => (
        <MenuItem {...optionProps} style={{ padding: 0 }}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={selected}
          />
          {props.getOptionLabel(option)}
        </MenuItem>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder={inputValue} />
      )}
      renderTags={() => null}
    />
  );
}

export default Multiselect;
